import {
  HOUSE_SELECTION_REASONS,
  selectHouseCandidate,
} from "@shrimpster/ad-services-contracts/house-selection";

export const HOUSE_PROMOTION_PLACEMENT = "home.latest.inline";
export const HOUSE_PROMOTION_TEXT_MIN = 20;
export const HOUSE_PROMOTION_TEXT_MAX = 180;
export const HOUSE_PROMOTION_MAX_EXCLUSIONS = 100;

const PUBLIC_SPACE_TYPES = new Set(["public-view", "public-interact"]);
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function text(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function httpsUrl(value = "") {
  try {
    const url = new URL(String(value || ""));
    return url.protocol === "https:" ? url.toString() : "";
  } catch {
    return "";
  }
}

export function validateHousePromotionHub(hub) {
  if (!hub || typeof hub !== "object" || Array.isArray(hub)) {
    return { eligible: false, reason: "space-missing" };
  }
  const spaceSlug = text(hub.slug || hub.id).toLowerCase();
  if (!SLUG_PATTERN.test(spaceSlug)) return { eligible: false, reason: "invalid-space-slug" };
  if (hub.publicReadable === false || !PUBLIC_SPACE_TYPES.has(String(hub.type || "public-view"))) {
    return { eligible: false, reason: "space-not-public" };
  }
  if (hub.adPromotion?.enabled !== true) return { eligible: false, reason: "promotion-disabled" };

  const promotionText = text(hub.adPromotion?.text);
  if (
    promotionText.length < HOUSE_PROMOTION_TEXT_MIN ||
    promotionText.length > HOUSE_PROMOTION_TEXT_MAX
  ) {
    return { eligible: false, reason: "invalid-promotion-text" };
  }

  const displayName = text(hub.displayName || hub.name);
  const bannerUrl = httpsUrl(hub.bannerUrl);
  const badgeUrl = httpsUrl(hub.badgeUrl);
  if (!displayName) return { eligible: false, reason: "missing-display-name" };
  if (!bannerUrl) return { eligible: false, reason: "missing-banner" };
  if (!badgeUrl) return { eligible: false, reason: "missing-badge" };

  return {
    eligible: true,
    value: {
      spaceSlug,
      displayName,
      promotionText,
      bannerUrl,
      badgeUrl,
    },
  };
}

export function buildHouseCatalogCandidate({ hub, weight = 1, placements } = {}) {
  const validation = validateHousePromotionHub(hub);
  if (!validation.eligible) return validation;
  const normalizedPlacements = Array.isArray(placements)
    ? [...new Set(placements.map(text).filter(Boolean))]
    : [HOUSE_PROMOTION_PLACEMENT];
  if (
    !Number.isInteger(weight) ||
    weight < 1 ||
    weight > 100 ||
    !normalizedPlacements.includes(HOUSE_PROMOTION_PLACEMENT)
  ) {
    return { eligible: false, reason: "invalid-selection-policy" };
  }
  return {
    eligible: true,
    value: {
      spaceSlug: validation.value.spaceSlug,
      weight,
      placements: normalizedPlacements,
    },
  };
}

export function isActiveHouseSiteBinding({ site, runtime, placementId } = {}) {
  return Boolean(
    site?.enabled === true &&
    runtime?.status === "active" &&
    typeof runtime.activeRevisionId === "string" &&
    runtime.activeRevisionId &&
    site.catalogRevisionId === runtime.activeRevisionId &&
    Array.isArray(site.allowedPlacements) &&
    site.allowedPlacements.includes(placementId)
  );
}

export function selectPublishedHouseCandidate({
  candidates,
  catalogRevision,
  opportunityId,
  seedNamespace,
  placementId = HOUSE_PROMOTION_PLACEMENT,
  portalAllowedHubSlugs = null,
  currentSpaceSlug = "",
  excludedSpaceSlugs = [],
} = {}) {
  if (placementId !== HOUSE_PROMOTION_PLACEMENT || !Array.isArray(candidates)) {
    return { eligible: false, reason: "invalid-placement" };
  }
  if (
    !Array.isArray(excludedSpaceSlugs) ||
    excludedSpaceSlugs.length > HOUSE_PROMOTION_MAX_EXCLUSIONS
  ) {
    return { eligible: false, reason: "invalid-exclusions" };
  }

  const portalAllowed = Array.isArray(portalAllowedHubSlugs)
    ? new Set(portalAllowedHubSlugs.map((slug) => text(slug).toLowerCase()).filter(Boolean))
    : null;
  const eligibleCandidates = candidates
    .filter((candidate) => (
      candidate &&
      (!portalAllowed || portalAllowed.has(text(candidate.spaceSlug).toLowerCase())) &&
      Array.isArray(candidate.placements) &&
      candidate.placements.includes(placementId)
    ))
    .map((candidate) => ({
      candidateId: text(candidate.spaceSlug).toLowerCase(),
      weight: candidate.weight,
    }));

  if (eligibleCandidates.length === 0) return { eligible: false, reason: "no-candidate" };

  const exclusions = [currentSpaceSlug, ...excludedSpaceSlugs]
    .map((slug) => text(slug).toLowerCase())
    .filter(Boolean);
  let selection = selectHouseCandidate({
    opportunityId,
    catalogRevision,
    seedNamespace,
    candidates: eligibleCandidates,
    excludedCandidateIds: exclusions,
  });

  // Page-level diversity must not turn otherwise valid House inventory into
  // no-fill after every candidate has appeared once.
  if (selection.reason === HOUSE_SELECTION_REASONS.ALL_EXCLUDED) {
    selection = selectHouseCandidate({
      opportunityId,
      catalogRevision,
      seedNamespace,
      candidates: eligibleCandidates,
      excludedCandidateIds: currentSpaceSlug ? [text(currentSpaceSlug).toLowerCase()] : [],
    });
  }

  return selection.selected
    ? { eligible: true, spaceSlug: selection.candidateId, catalogRevision }
    : { eligible: false, reason: selection.reason || "no-candidate" };
}

export function buildHouseRenderPayload({
  hub,
  canonicalOrigin,
  placementId = HOUSE_PROMOTION_PLACEMENT,
} = {}) {
  if (placementId !== HOUSE_PROMOTION_PLACEMENT) return null;
  const validation = validateHousePromotionHub(hub);
  if (!validation.eligible) return null;
  let destinationUrl;
  try {
    destinationUrl = new URL(
      `/h/${encodeURIComponent(validation.value.spaceSlug)}`,
      canonicalOrigin
    ).toString();
  } catch {
    return null;
  }
  return {
    spaceSlug: validation.value.spaceSlug,
    displayName: validation.value.displayName,
    promotionText: validation.value.promotionText,
    bannerUrl: validation.value.bannerUrl,
    badgeUrl: validation.value.badgeUrl,
    callToAction: "Explore space",
    destinationUrl,
  };
}
